(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Ftc01BackgroundVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.video = this.querySelector('[data-video]');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.video || !this.toggle) return;

      this.visible = false;
      this.manuallyPaused = false;
      this.onToggle = this.onToggle.bind(this);
      this.onVisibilityChange = this.sync.bind(this);
      this.onPlaybackChange = this.updateToggle.bind(this);
      this.onMotionChange = this.sync.bind(this);
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.15;
        this.sync();
      }, { threshold: [0, 0.15, 0.5] });

      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.toggle.addEventListener('click', this.onToggle);
      this.video.addEventListener('play', this.onPlaybackChange);
      this.video.addEventListener('pause', this.onPlaybackChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      reduceMotion.addEventListener?.('change', this.onMotionChange);
      this.observer.observe(this.video);
      this.updateToggle();
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      this.video?.removeEventListener('play', this.onPlaybackChange);
      this.video?.removeEventListener('pause', this.onPlaybackChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      reduceMotion.removeEventListener?.('change', this.onMotionChange);
      this.observer?.disconnect();
      this.video?.pause();
      this.dataset.initialized = 'false';
    }

    onToggle() {
      if (this.video.paused) {
        this.manuallyPaused = false;
        this.video.dataset.userPlay = 'true';
        this.video.play().catch(() => this.updateToggle());
      } else {
        this.manuallyPaused = true;
        this.video.pause();
      }
    }

    sync() {
      const canAutoplay = this.visible
        && !document.hidden
        && !this.manuallyPaused
        && (!reduceMotion.matches || this.video.dataset.userPlay === 'true');
      if (canAutoplay) this.video.play().catch(() => this.updateToggle());
      else this.video.pause();
    }

    updateToggle() {
      const paused = this.video.paused;
      this.toggle.textContent = paused ? 'Play' : 'Pause';
      this.toggle.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} background video`);
    }
  }

  class Ftc01VideoDemo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.video = this.querySelector('[data-demo-video]');
      this.toggle = this.querySelector('[data-video-toggle]');
      this.tabs = Array.from(this.querySelectorAll('[data-demo-tab]'));
      this.rows = Array.from(this.querySelectorAll('[data-demo-row]'));
      this.count = this.querySelector('[data-demo-count]');
      this.panel = this.querySelector('[data-demo-panel]');
      this.prev = this.querySelector('[data-demo-prev]');
      this.next = this.querySelector('[data-demo-next]');
      this.data = JSON.parse(this.querySelector('[data-demo-data]')?.textContent || '[]');
      if (!this.video || !this.toggle || this.data.length === 0) return;

      this.index = 0;
      this.visible = false;
      this.manuallyPaused = false;
      this.touchStart = null;
      this.cleanups = [];
      this.listen = (element, event, handler, options) => {
        element?.addEventListener(event, handler, options);
        if (element) this.cleanups.push(() => element.removeEventListener(event, handler, options));
      };

      this.tabs.forEach((tab, index) => {
        this.listen(tab, 'click', () => this.select(index));
        this.listen(tab, 'keydown', (event) => this.onTabKeydown(event, index));
      });
      this.listen(this.prev, 'click', () => this.select(this.index - 1));
      this.listen(this.next, 'click', () => this.select(this.index + 1));
      this.listen(this.toggle, 'click', () => this.onToggle());
      this.listen(this.video, 'play', () => this.updateToggle());
      this.listen(this.video, 'pause', () => this.updateToggle());
      this.listen(this.video, 'ended', () => {
        if (!this.manuallyPaused && this.visible && !document.hidden) this.select(this.index + 1);
      });
      this.listen(this.panel, 'touchstart', (event) => this.onTouchStart(event), { passive: true });
      this.listen(this.panel, 'touchmove', (event) => {
        if (event.touches.length !== 1) this.touchStart = null;
      }, { passive: true });
      this.listen(this.panel, 'touchend', (event) => this.onTouchEnd(event), { passive: true });
      this.listen(this.panel, 'touchcancel', () => { this.touchStart = null; }, { passive: true });
      this.listen(document, 'visibilitychange', () => this.sync());
      if (reduceMotion.addEventListener) this.listen(reduceMotion, 'change', () => this.sync());

      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.15;
        this.sync();
      }, { threshold: [0, 0.15, 0.5] });
      this.observer.observe(this.video);
      this.resizeObserver = new ResizeObserver(() => this.fitMobileTitles());
      this.resizeObserver.observe(this.querySelector('[data-demo-descriptions]'));
      document.fonts?.ready.then(() => this.fitMobileTitles());
      this.select(0);
    }

    disconnectedCallback() {
      this.cleanups?.forEach((cleanup) => cleanup());
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.video?.pause();
      this.dataset.initialized = 'false';
    }

    select(next, focus = false) {
      this.index = (next + this.data.length) % this.data.length;
      const item = this.data[this.index];
      this.video.pause();
      this.video.replaceChildren();
      item.sources.forEach((source) => {
        const element = document.createElement('source');
        element.src = source.url;
        element.type = source.mimeType;
        this.video.appendChild(element);
      });
      this.video.poster = item.poster || '';
      this.video.setAttribute('aria-label', `${item.title} Video demonstration`);
      this.video.load();
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('aria-selected', String(index === this.index));
        tab.tabIndex = index === this.index ? 0 : -1;
      });
      this.rows.forEach((row, index) => { row.dataset.active = String(index === this.index); });
      this.count.textContent = `${this.index + 1} / ${this.data.length}`;
      this.panel.setAttribute('aria-labelledby', this.tabs[this.index]?.id || '');
      this.manuallyPaused = false;
      if (focus) this.tabs[this.index]?.focus();
      this.sync();
    }

    onTabKeydown(event, index) {
      const target = {
        ArrowDown: index + 1,
        ArrowUp: index - 1,
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: this.data.length - 1
      }[event.key];
      if (target === undefined) return;
      event.preventDefault();
      this.select(target, true);
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) {
        this.touchStart = null;
        return;
      }
      const touch = event.touches[0];
      this.touchStart = { x: touch.clientX, y: touch.clientY };
    }

    onTouchEnd(event) {
      if (!this.touchStart || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.select(this.index + (dx < 0 ? 1 : -1));
      }
    }

    onToggle() {
      if (this.video.paused) {
        this.manuallyPaused = false;
        this.video.dataset.userPlay = 'true';
        this.video.play().catch(() => this.updateToggle());
      } else {
        this.manuallyPaused = true;
        this.video.pause();
      }
    }

    sync() {
      const canAutoplay = this.visible
        && !document.hidden
        && !this.manuallyPaused
        && (!reduceMotion.matches || this.video.dataset.userPlay === 'true');
      if (canAutoplay) this.video.play().catch(() => this.updateToggle());
      else this.video.pause();
    }

    updateToggle() {
      const paused = this.video.paused;
      this.toggle.textContent = paused ? 'Play' : 'Pause';
      this.toggle.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} demonstration video`);
    }

    fitMobileTitles() {
      const container = this.querySelector('[data-demo-descriptions]');
      if (!container || !window.matchMedia('(max-width: 760px)').matches) {
        container?.style.removeProperty('--ftc01-demo-title-size');
        return;
      }
      const width = container.clientWidth;
      const title = this.rows[0]?.querySelector('h3');
      if (!width || !title) return;
      const context = document.createElement('canvas').getContext('2d');
      const style = getComputedStyle(title);
      context.font = `${style.fontWeight} 16px ${style.fontFamily}`;
      const widest = Math.max(...this.data.map((item) => context.measureText(item.title).width));
      const size = Math.floor(Math.min(16, ((width - 2) * 16) / widest) * 10) / 10;
      container.style.setProperty('--ftc01-demo-title-size', `${size}px`);
    }
  }

  if (!customElements.get('ftc01-background-video')) {
    customElements.define('ftc01-background-video', Ftc01BackgroundVideo);
  }
  if (!customElements.get('ftc01-video-demo')) {
    customElements.define('ftc01-video-demo', Ftc01VideoDemo);
  }
})();
